import { Box } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Localized, PoolButton, PoolTypography, PreviewImage } from '@synergy/components';
import { PaloAmount, PaloColumn, PaloListView } from '@synergy/components-palo';
import { getLocalizedString } from '@synergy/utils';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import * as SupplierSpecialOffersProductsActions from './store/supplier-special-offers-detail-products-actions';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import { SpecialOfferProduct } from './models/special-offer-product';
import { localesSelector } from '../../../../../common/selectors/locale-selectors';
import { supplierSpecialOffersDetailProductsSelector } from './store/supplier-special-offers-detail-products-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

export const SupplierSpecialOffersDetailProducts = () => {
    const supplierSpecialOffer = useAppSelector(
        state => state.contentManagement.supplierSpecialOffers.detail.overview.supplierSpecialOffer
    );
    const products = useAppSelector(supplierSpecialOffersDetailProductsSelector);
    const dispatch = useThunkDispatch();
    const { currency } = useAppSelector(state => state.settings);
    const [searchParams] = useSearchParams();
    const locales = useAppSelector(localesSelector);
    const navigate = useNavigate();

    useEffect(() => {
        if (supplierSpecialOffer?.id) {
            dispatch(
                SupplierSpecialOffersProductsActions.fetchSupplierSpecialOffersProducts(
                    supplierSpecialOffer.id,
                    searchParams
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, supplierSpecialOffer?.id, searchParams]);

    const columns: PaloColumn<SpecialOfferProduct>[] = [
        {
            key: 'preview',
            type: 'icon',
            head: '',
            cell: item =>
                item.previewImage && (
                    <PreviewImage url={item.previewImage.path} alt={getLocalizedString(locales, item.articleName)} />
                ),
        },
        {
            key: 'number',
            type: 'articleNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            cell: item => item.number,
        },
        {
            key: 'name',
            type: 'name',
            head: <FormattedMessage id="app.table.column.article" />,
            cell: item => <Localized value={item.articleName} />,
        },
        {
            key: 'currentPrice',
            type: 'price',
            currency,
            head: <FormattedMessage id="app.table.column.originalPrice" />,
            cell: item => (
                <PaloAmount value={item.mainPrice[currency] ?? 0} maximumFractionDigits={4} />
            ),
        },
        {
            key: 'specialPrice',
            type: 'price',
            currency,
            head: <FormattedMessage id="app.table.column.specialPrice" />,
            cell: item => <PaloAmount value={item.specialPrice} maximumFractionDigits={4} />,
        },
    ];

    const handleRowClicked = (id: number) => {
        navigate(ROUTES.products.product.detail.replace(':id', id.toString()));
    };

    const handleNavigateToArticles = () => {
        const searchParams = new URLSearchParams();
        if (supplierSpecialOffer?.supplier?.id) {
            searchParams.append('supplierId', supplierSpecialOffer.supplier.id.toString());
        }
        navigate({ pathname: ROUTES.products.product.base, search: searchParams.toString() });
    };

    if (0 === products.items.length) {
        return (
            <Box sx={{ px: theme => theme.general.pageContentSpacing, py: 2 }}>
                <PoolTypography id="app.common.emptyResult" />
                <br />
                <PoolButton variant="contained" color="primary" onClick={handleNavigateToArticles}>
                    <FormattedMessage id="app.contentManagement.supplierSpecialOffers.navigateToArticles" />
                </PoolButton>
            </Box>
        );
    }

    return (
        <PaloListView<SpecialOfferProduct, number>
            embedded={true}
            columns={columns}
            items={products.items}
            getRowId={item => item.id}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
            onRowClick={item => handleRowClicked(item.id)}
        />
    );
};
